import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LandingFeatures } from "../components/LandingFeatures";
import { DemoRequestForm } from "../components/DemoRequestForm";
import { VsmChat, savedVsmSession } from "../components/VsmChat";

const VSM_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_7fa3bc92-7665-4bcb-955d-842403bc074a/artifacts/e443d54c74d3619e_05.png";

function LoginCard() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <form onSubmit={submit} className="w-full border border-zinc-900 bg-white p-6 shadow-[6px_6px_0_0_#18181b]" data-testid="login-form">
      <h2 className="font-grotesk text-2xl font-bold tracking-tight mb-1">Inloggen</h2>
      <p className="text-sm text-zinc-500 mb-5">Toegang tot je organisatiemodellen.</p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-widest">E-mail</Label>
          <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-none border-zinc-900 focus-visible:ring-zinc-900" required />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Wachtwoord</Label>
          <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-none border-zinc-900 focus-visible:ring-zinc-900" required />
        </div>
      </div>
      {error && <p data-testid="login-error" className="text-sm text-red-600 mt-3">{error}</p>}
      <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full mt-5 rounded-none bg-zinc-900 hover:bg-zinc-700 h-11 tracking-wide">
        {loading ? "Bezig…" : "Inloggen"}
      </Button>
    </form>
  );
}

export default function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(savedVsmSession);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const toDemo = () => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <header className="border-b border-zinc-900 h-16 flex items-center justify-between px-6 lg:px-12 sticky top-0 bg-white/90 backdrop-blur-md z-20">
        <div className="font-grotesk font-bold tracking-tight text-lg">CIRCUMPLEX · VSM</div>
        <Button onClick={toDemo} variant="outline" className="rounded-none border-zinc-900 h-9" data-testid="header-demo-button">Doe de gratis VSM-test</Button>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-[1.25fr_1fr] gap-14 items-center">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">Organisatiemodellering · OBM · Viable System Model</div>
          <h1 className="font-grotesk text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
            De ultieme tool om je organisatie levensvatbaar te modelleren.
          </h1>
          <p className="text-base md:text-lg text-zinc-600 mt-6 max-w-xl leading-relaxed">
            Modelleer je organisatie in het Viable System Model en breng daarna alles in kaart: strategieën en het gedrag eronder,
            vaardigheden van medewerkers, prestatieverbetering met OBM, de belasting van je mensen en hoe het met ze gaat.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={toDemo} className="rounded-none bg-zinc-900 hover:bg-zinc-700 h-12 px-6 text-base" data-testid="hero-demo-button">
              Start de gratis VSM-test <ArrowDown className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <div className="mt-10 max-w-sm"><LoginCard /></div>
        </div>
        <div className="relative animate-in fade-in duration-1000">
          <img src={VSM_IMG} alt="Het Circumplex Viable System Model" className="w-full max-w-lg mx-auto" data-testid="login-vsm-image" />
          <div className="absolute -bottom-4 left-0 font-mono text-[10px] uppercase tracking-widest text-zinc-400">Fig. 1 — Circumplex VSM, recursief via Systeem I</div>
        </div>
      </section>

      <LandingFeatures />

      <section id="demo" className="bg-zinc-950 text-white scroll-mt-16" data-testid="landing-demo-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-14">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-6">Gratis VSM-test</div>
            <h2 className="font-grotesk text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Hoe sterk is elk systeem van jouw organisatie?</h2>
            <p className="text-zinc-300 mt-6 leading-relaxed max-w-md">
              Ga meteen gratis aan de slag met Robin, onze AI buddy. In een gesprek van 20 vragen over de meest voorkomende
              organisatieproblemen berekent Robin hoe sterk Systeem 1 tot en met 5 van het Viable System Model in jouw organisatie zijn.
              Robin legt onderweg uit hoe het werkt en bespreekt daarna elk systeem met je. Gebaseerd op het werk van Stafford Beer en
              Patrick Hoverstadt (The Fractal Organization).
            </p>
            <ol className="mt-10 space-y-4 font-mono text-sm text-zinc-400">
              <li><span className="text-white">01</span> — Vul je gegevens in</li>
              <li><span className="text-white">02</span> — Beantwoord de vragen van Robin, zo kort of uitgebreid als je wilt</li>
              <li><span className="text-white">03</span> — Bekijk de uitslag per systeem; daarna nemen we contact op voor de verdere stappen</li>
            </ol>
          </div>
          {session ? <VsmChat session={session} /> : <DemoRequestForm onStarted={setSession} />}
        </div>
      </section>

      <footer data-testid="landing-footer" className="border-t border-zinc-900 px-6 lg:px-12 py-6 font-mono text-xs text-zinc-500 flex flex-col sm:flex-row gap-2 sm:justify-between">
        <span>CIRCUMPLEX · VSM</span>
        <span data-testid="footer-company-info">De VSM-applicatie is ontwikkeld en wordt aangeboden door TIOUW.com BV · Binderij 7-L · 1185 ZH Amstelveen</span>
        <span>© {new Date().getFullYear()} TIOUW.com BV</span>
      </footer>
    </div>
  );
}
